import { AsyncPipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FaceLandmarker, FilesetResolver, ImageEmbedder, ObjectDetector } from '@mediapipe/tasks-vision';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AlertComponent, ButtonComponent, PwcsLayoutComponent, QrComponent, TileComponent } from '@tecalisdev/infinity';
import {
	catchError,
	combineLatest,
	firstValueFrom,
	interval,
	of,
	retry,
	Subject,
	switchMap,
	take,
	takeUntil,
	throwError,
	timer,
} from 'rxjs';
import { IModalClose } from '../core/models/modal-confirmation.models';
import { AuthService } from '../core/services/auth.service';
import { TWO_FACTOR_PENDING_ERROR_CODE } from '../core/models/auth.models';
import { DialogConfirmationService } from '../core/services/dialog-confirmation.service';
import { LanguageService } from '../core/services/language.service';
import { LoadingService } from '../core/services/loading.service';
import { ErrorComponent } from '../shared/components/error/error.component';
import { IMaster } from '../shared/models/master.models';
import { AudioService, GuideAudioReloadScope } from '../shared/services/audio.service';
import { CameraService } from '../shared/services/camera.service';
import { CloudLogsService, LogType } from '../shared/services/cloud-logs.service';
import { ConfigService } from '../shared/services/config/config.service';
import { DesignService } from '../shared/services/config/design.service';
import { ErrorService } from '../shared/services/error.service';
import { IdentitySDKService } from '../shared/services/identity-sdk.service';
import { MasterService } from '../shared/services/master.service';
import { OperationService } from '../shared/services/step.service';
import { StreamingManagementService } from '../shared/services/streaming-management.service';
import { UserAgentService } from '../shared/services/user-agent.service';
import { GeolocationPermissionService } from '../shared/services/geolocation-permission.service';
import { DynamicFormComponent } from './components/dynamic-form/dynamic-form.component';
import { ChooseComponent } from './components/choose/choose.component';
import { BankScoringComponent } from './components/connections/bank-scoring/bank-scoring.component';
import { IrpfComponent } from './components/connections/irpf/irpf.component';
import { WorkLifeComponent } from './components/connections/work-life/work-life.component';
import { DocsComponent } from './components/docs/docs.component';
import { FinishComponent } from './components/finish/finish.component';
import { HomeComponent } from './components/home/home.component';
import { LifeCheckComponent } from './components/life-check/life-check.component';
import { ManualLifeCheckComponent } from './components/manual-life-check/manual-life-check.component';
import { ManualOcrComponent } from './components/manual-ocr/manual-ocr.component';
import { OcrCannyComponent } from './components/ocr-canny/ocr-canny.component';
import { OcrComponent } from './components/ocr/ocr.component';
import {
	CONNECTION_TYPE,
	ProcessLog,
	IHomeOperation,
	IOperation,
	IFinishOperation,
	IProcessConfigResponse,
	IProcessStatusResponse,
	MODE,
	OPERATION_TYPE,
	PROCESS_EXPIRED_DETAILS_CODE,
	PROCESS_STATUS,
	IProcessCheckStatusResponse,
	IProcessDesign,
} from './models/user-identification';
import { OcrService } from './services/ocr/ocr.service';
import { LifeCheckService } from './services/pol/life-check.service';
import { ProcessHelperService } from './services/process-helper.service';
import { MEDIAPIPE_TASKS_VISION_WASM_CDN } from '../shared/constants/mediapipe.constants';
import { resolveMediapipeDelegate } from '../shared/utils/mediapipe-face-delegate.util';
import { ProcessTestsHelperService } from './services/process-tests-helper.service';
import { ProcessService } from './services/process.service';
import {
	FINISH_REDIRECT_DELAY_MS,
	finishHasRedirectUrls,
	isDefinitiveDeviceStatus,
	isProcessExpiredCode,
	pickFinishOperation,
	resolveDeviceStatusForExpiry,
	shouldScheduleExpiredRedirect,
} from './utils/expired-redirect.util';
import { resolveFinishResultMessage } from './utils/resolve-finish-result-message.util';
import { captureUserActivationAtEntry, waitForRealUserVisit } from '../shared/utils/wait-for-real-user-visit.util';
import { DeviceNotAllowedComponent } from '../shared/components/device-not-allowed/device-not-allowed.component';
import { ScreenshotService } from '../core/services/screenshot.service';
import { DelayedProcessWarningComponent } from '../shared/components/delayed-process-warning/delayed-process-warning.component';
import { ScriptureComponent } from './components/connections/scripture/scripture.component';
import { TwoFactorModalComponent } from './components/two-factor-modal/two-factor-modal.component';
import { ProcessTabGuardService } from '../core/services/process-tab-guard.service';
import { ERROR_CODE, ERROR_STATUS } from '../shared/models/error.models';
declare let cv;

/** Tras este delay, si el gate anti-prefetch sigue bloqueado, se muestra el prompt de retomar (NIF-790). */
const REAL_USER_VISIT_HINT_DELAY_MS = 3000;
/** Tras el gesto (pantalla o link), spinner sin copy al menos este tiempo. */
const REAL_USER_VISIT_EMPTY_TEXT_MS = 350;

@Component({
	selector: 'app-user-identification',
	imports: [
		AsyncPipe,
		TranslatePipe,
		NgClass,
		AlertComponent,
		ButtonComponent,
		PwcsLayoutComponent,
		TileComponent,
		HomeComponent,
		OcrComponent,
		ManualOcrComponent,
		OcrCannyComponent,
		LifeCheckComponent,
		ManualLifeCheckComponent,
		DocsComponent,
		DynamicFormComponent,
		ChooseComponent,
		WorkLifeComponent,
		BankScoringComponent,
		IrpfComponent,
		FinishComponent,
		ErrorComponent,
		DeviceNotAllowedComponent,
		DelayedProcessWarningComponent,
		QrComponent,
		ScriptureComponent,
		TwoFactorModalComponent,
	],
	templateUrl: './user-identification.component.html',
	styleUrls: ['./user-identification.component.scss'],
})
export class UserIdentificationComponent implements OnInit, OnDestroy {
	processTestHelper = inject(ProcessTestsHelperService);
	private readonly processTabGuard = inject(ProcessTabGuardService);
	private designAssetRefreshTimer: ReturnType<typeof setTimeout> | null = null;

	@HostListener('window:resize', ['$event'])
	onResize(event: Event) {
		const screenWidth = (event.target as Window).innerWidth;
		const design = this.process?.design ?? this.lastAppliedDesign;
		if (design?.watermark) {
			this.designService.patchShowWatermark(screenWidth > 768);
		}
	}

	public operationType = OPERATION_TYPE;
	public connectionType = CONNECTION_TYPE;
	public processStatus = PROCESS_STATUS;
	public MODE = MODE;
	private isDeviceOnStandby: boolean = false;
	/** Evita pedir nonce/register repetidos en la misma sesión de página. */
	private deviceRegisterCompleted = false;
	private destroy$: Subject<void> = new Subject<void>();
	private checkStatus$: Subject<void> = new Subject<void>();
	private expiredRedirectScheduled = false;
	/** Indica si el proceso ya venia retomado en el momento de cargar la config. */
	private resumedFromConfigLoad = false;
	private audioCheckInterval: any;
	// Models
	private tasksVision: any;
	private runningMode: 'IMAGE' | 'VIDEO' = 'IMAGE';
	public operations: Array<IOperation> = [];
	public process: IProcessConfigResponse;
	/** Diseño aplicado desde `/process/config_design` si `process.design` aún no está cargado. */
	private lastAppliedDesign: IProcessDesign | null = null;
	// Streaming
	private isStreamingActive: boolean = false;
	private lastOperationType: OPERATION_TYPE | null = null;
	private currentStreamId: string | null = null; // StreamId activo actualmente
	private isHandlingStreaming: boolean = false; // Guard para prevenir ejecuciones concurrentes
	/** NIF-1064: una sola notificación with_stream por proceso (OCR↔POL). */
	private streamingStartedNotified = false;
	/** Modal 2FA: visible según `process['2fa']` (raíz). */
	public isTwoFactorModalVisible = false;
	/** El usuario completó la verificación 2FA (el modal ya se cerró). */
	private twoFactorVerified = false;
	/** Canal 2FA cuando `config` devuelve 2102 antes de tener el payload completo. */
	private pendingTwoFactorChannel: 'sms' | 'whatsapp' | null = null;
	/** Polling de `/process/status` ya arrancado (evita duplicar interval tras TFA + reload config). */
	private checkStatusPollingStarted = false;

	constructor(
		public designService: DesignService,
		public errorService: ErrorService,
		public auth: AuthService,
		private loadingService: LoadingService,
		private processHelperService: ProcessHelperService,
		private operationService: OperationService,
		private route: ActivatedRoute,
		private cloudLogsService: CloudLogsService,
		private screenShotService: ScreenshotService,
		private trans: TranslateService,
		private cameraService: CameraService,
		private lifeCheckService: LifeCheckService,
		private ocrService: OcrService,
		private configService: ConfigService,
		private processService: ProcessService,
		private dialogConfirmationService: DialogConfirmationService,
		private masterService: MasterService,
		private languageService: LanguageService,
		private streamingManagementService: StreamingManagementService,
		private identitySDKService: IdentitySDKService,
		private audioService: AudioService,
		private userAgentService: UserAgentService,
		private geolocationPermissionService: GeolocationPermissionService,
	) {}

	ngOnInit(): void {
		combineLatest([this.route.data, this.route.queryParams]).subscribe(
			([data, params]: [{ iframe: boolean; operations: Array<OPERATION_TYPE> }, { [key: string]: string }]) => {
				// Lógica para params
				if (params['tkn']) {
					// Token de acceso para el resto de llamadas
					this.auth.setAccessToken(params['tkn']);
					// Token para el qr de cambiar de dispositivo
					this.auth.setAuthToken(params['tkn']);
					const url = new URL(window.location.href);
					url.searchParams.delete('tkn');
					window.history.replaceState({}, document.title, url.toString());
				}

				// Lógica para data
				if (data?.operations?.length) {
					// Sección de prueba para el desarrollo
					this.designService.setConfigDesign(this.processTestHelper.processTestsDesign);
					this.configService.setConfig(this.processTestHelper.processTestsConfig);
					this.operationService.setOperations(this.processTestHelper.getTestsOperations(data));
					this.process = {
						config: this.processTestHelper.processTestsConfig,
						design: this.processTestHelper.processTestsDesign,
						operations: this.processTestHelper.getTestsOperations(data),
						status_process_device: PROCESS_STATUS.INPROGRESS,
						process: 'test-process',
					} as IProcessConfigResponse;
					this.resumedFromConfigLoad = this.isResumeContext(this.process);
					this.languageService.setCurrentProcessId(this.process.process);
					this.languageService.setAllowedLanguages(['es', 'en', 'fr', 'it', 'pt']);
					this.languageService.applyLanguageForCurrentProcess();
					this.loadTaskVision();
					this.loadingService.hide();
				} else {
					this.getInitialData();
				}
			},
		);

		this.processService.getUnsubcribeCheckStatus.subscribe((change: boolean) => {
			if (change) {
				this.unSubscribeCheckStatus();
			}
		});

		// Suscribirse a los cambios de operations desde los componentes hijos
		this.operationService
			.getOperations()
			.pipe(takeUntil(this.destroy$))
			.subscribe((operations: IOperation[]) => {
				if (operations && operations.length > 0) {
					const previousOperation = this.operations.find((op: IOperation) => !op.completed);
					const currentOperation = operations.find((op: IOperation) => !op.completed);

					// Actualizar referencia (y process.operations: Finish NIF-969 lee process, no solo operations).
					this.operations = [...operations];
					if (this.process) {
						this.process = { ...this.process, operations: this.operations };
					}

					// Manejar cambios de streaming basado en la operación actual
					if (currentOperation) {
						this.prepareCameraHandoffForOperationTransition(previousOperation, currentOperation);
						// No await para no bloquear la transición de operaciones
						// El guard interno previene ejecuciones concurrentes
						this.handleStreamingForOperation(previousOperation, currentOperation);
					}
					this.evaluateTwoFactorModal();
				}
			});
	}

	public async getInitialData(): Promise<void> {
		captureUserActivationAtEntry();
		this.deviceRegisterCompleted = false;
		this.processService.resetDeviceRegistrationGate();
		await this.auth.resolveDeviceId();
		await this.userAgentService.resolveAndCacheEnrichedUserAgent();
		// Cargar diseño antes de cualquier loader para que el spinner no sea el por defecto.
		const design = await firstValueFrom(this.processService.getConfigDesign().pipe(catchError(() => of(null))));
		if (design) this.applyProcessDesign(design);
		// Esperar al checkProcessStatus antes de cargar la config para manejar casos
		// 	donde el proceso tenga la configuración de reiniciar el proceso
		await this.checkProcessStatus();
		await this.loadProcessConfig();
	}

	/**
	 * NIF-650: candado de visita real + register (sin spinner CONFIG) → un solo getConfig.
	 * Evita pintar «iniciar» en bootstrap y luego «retomar» tras el claim.
	 * NIF-790: si nonce/register falla con JWT 1002/1003, leer config (allowExpired en back)
	 * antes del tile «Proceso caducado» del error genérico.
	 */
	private async loadProcessConfig(): Promise<void> {
		try {
			await this.ensureDeviceRegistered();
			this.fetchProcessConfig();
		} catch (error: unknown) {
			this.loadingService.hide();
			if (this.isSessionTokenExpiredHttpError(error)) {
				this.loadingService.show(this.trans.instant('GENERAL.LOADING.CONFIG'));
				this.fetchProcessConfig();
				return;
			}
			this.errorService.hasGeneralError({
				...(error as HttpErrorResponse)?.error,
				action: () => {
					void this.getInitialData();
				},
			});
			this.cloudLogsService.sendCloudLogs(
				{ deviceRegisterBeforeConfig: `Error: ${error}` },
				ProcessLog.PRELOAD,
				LogType.Error,
			);
		}
	}

	/** JWT 1002/1003 en respuestas HTTP (nonce/register/status). */
	private isSessionTokenExpiredHttpError(error: unknown): boolean {
		const code = Number((error as HttpErrorResponse)?.error?.error?.error_code);
		return code === 1002 || code === 1003;
	}

	/**
	 * Registro explícito tras visita real (evita prefetch de Gmail).
	 * NIF-790: tras `config_design` ya hay branding → spinner durante el wait (no pantalla en blanco).
	 * Si el gate tarda ~3 s → prompt «¿Todo sigue igual?» + link retomar;
	 * tras gesto (pantalla o link) → al menos 1 s sin texto; luego CONFIG + register.
	 */
	private async ensureDeviceRegistered(): Promise<void> {
		if (this.deviceRegisterCompleted) {
			return;
		}

		const configText = this.trans.instant('GENERAL.LOADING.CONFIG');
		this.loadingService.show(configText);

		let waiting = true;
		let hintTimerId: ReturnType<typeof setTimeout> | null = null;

		const scheduleHint = (): void => {
			if (hintTimerId !== null) {
				clearTimeout(hintTimerId);
			}
			hintTimerId = setTimeout(() => {
				hintTimerId = null;
				if (!waiting) {
					return;
				}
				this.loadingService.patchLoading({ showResumePrompt: true });
			}, REAL_USER_VISIT_HINT_DELAY_MS);
		};

		const resumeSub = this.loadingService.resumeClick$.subscribe(() => {
			if (!waiting) {
				return;
			}
			if (hintTimerId !== null) {
				clearTimeout(hintTimerId);
				hintTimerId = null;
			}
			this.loadingService.patchLoading({ showResumePrompt: false, hideTextKeepSpace: true });
		});

		scheduleHint();

		const waitStartedAt = Date.now();
		try {
			await waitForRealUserVisit();
		} finally {
			waiting = false;
			if (hintTimerId !== null) {
				clearTimeout(hintTimerId);
			}
			resumeSub.unsubscribe();
		}

		// Gate bloqueado → gesto del usuario. Copy invisible ≥ 1 s (hueco tipográfico intacto).
		if (Date.now() - waitStartedAt >= 50) {
			this.loadingService.patchLoading({
				text: configText,
				showResumePrompt: false,
				hideTextKeepSpace: true,
			});
			await new Promise<void>((resolve) => setTimeout(resolve, REAL_USER_VISIT_EMPTY_TEXT_MS));
		}

		this.loadingService.show(configText);
		const { nonce } = await firstValueFrom(this.processService.getDeviceRegisterNonce());
		await firstValueFrom(this.processService.registerDevice(nonce));
		this.deviceRegisterCompleted = true;
		this.processService.markDeviceRegistered();
	}

	/** Aplica `IProcessDesign` al servicio de diseño (misma semántica que tras `/process/config`). */
	private applyProcessDesign(design: IProcessDesign, assetLayer: 'primary' | 'fallback' = 'primary'): void {
		this.lastAppliedDesign = design;
		this.designService.setConfigDesign({ ...design, showWatermark: design.watermark }, assetLayer);
		if (assetLayer === 'primary') this.scheduleDesignAssetRefresh();
	}

	private scheduleDesignAssetRefresh(): void {
		if (this.designAssetRefreshTimer !== null) {
			clearTimeout(this.designAssetRefreshTimer);
		}
		const delayMs = this.designService.getDesignAssetRefreshDelayMs();
		if (delayMs === null) return;
		this.designAssetRefreshTimer = setTimeout(() => {
			this.designAssetRefreshTimer = null;
			this.processService
				.getConfigDesign()
				.pipe(catchError(() => of(null)))
				.subscribe((design) => {
					if (design) {
						this.applyProcessDesign(design, 'primary');
					}
				});
		}, delayMs);
	}

	private initCheckStatus(): void {
		interval(10000)
			.pipe(
				takeUntil(this.checkStatus$),
				switchMap(() =>
					this.processService.getProcessStatus().pipe(
						retry({ count: 2, delay: 3000 }),
						catchError((error: HttpErrorResponse) => {
							// Esta lógica se ejecuta después de 3 intentos fallidos consecutivos
							this.unSubscribeCheckStatus();
							return throwError(() => error);
						}),
					),
				),
			)
			.subscribe({
				next: (response: IProcessStatusResponse) => {
					this.syncProcessStatusCode(response.process_status_code);
					const deviceStatus = resolveDeviceStatusForExpiry(
						response.status_process_device,
						response.process_status_code,
					);
					const isDefinitiveStatus = isDefinitiveDeviceStatus(deviceStatus);
					// En dispositivos no activos, detener capturas periódicas para evitar ruido y consumo.
					this.syncScreenshotCaptureForDeviceStatus(deviceStatus);
					if (isDefinitiveStatus) {
						this.unSubscribeCheckStatus();

						// Enviar logs con screenshot cuando el status sea KO o CANCELLED
						if (deviceStatus === PROCESS_STATUS.KO || deviceStatus === PROCESS_STATUS.CANCELLED) {
							this.screenShotService.saveScreenshot(
								{ status_process_device: deviceStatus },
								ProcessLog.PROCESS_STATUS,
							);
							this.cloudLogsService.sendCloudLogs(
								{ ...response },
								ProcessLog.PROCESS_STATUS,
								LogType.Warning,
							);
						}

						// Solo notificar el estado del proceso (sin FLOW_COMPLETE)
						// FLOW_COMPLETE se notifica en finish.component cuando se completa el redirect
						if (deviceStatus === PROCESS_STATUS.OK) {
							this.identitySDKService.notifyProcessSuccess({
								status: deviceStatus,
								process: this.process.process,
							});
						} else {
							this.identitySDKService.notifyProcessError(
								`Process finished with status: ${deviceStatus}`,
								{
									status: deviceStatus,
									process: this.process.process,
								},
							);
						}
					}

					// Recargar la configuración si se retoma
					if (
						this.process.status_process_device !== response.status_process_device &&
						response.status_process_device === PROCESS_STATUS.INPROGRESS
					) {
						this.updateConfig();
						// Retomar streaming si es requerido
						if (this.process.config.required_recording) this.resumeStreaming();
					}

					if (response.status_process_device === PROCESS_STATUS.STANDBY) {
						this.processHelperService.stopAllOperationsTimers();
						this.isDeviceOnStandby = true;
					} else if (response.status_process_device === PROCESS_STATUS.INPROGRESS) {
						this.isDeviceOnStandby = false;
					}

					// Detener el streaming si el dispositivo no está en INPROGRESS
					if (this.isStreamingActive && response.status_process_device !== PROCESS_STATUS.INPROGRESS) {
						this.streamingManagementService.stopStreaming();
						this.isStreamingActive = false;
					}

					if (isDefinitiveStatus && this.audioService.isPlaying) {
						console.error('Audio isPlaying');
						this.startAudioCheckInterval(deviceStatus);
						return;
					}
					this.process.status_process_device = deviceStatus;
					if (deviceStatus === PROCESS_STATUS.CANCELLED && this.maybeRedirectOnExpired()) {
						return;
					}
				},
				error: (error: HttpErrorResponse) => {
					const onFinish =
						this.operations?.find((op: IOperation) => !op.completed)?.type === OPERATION_TYPE.FINISH;
					if (this.isTwoFactorGateActive() || onFinish) {
						this.unSubscribeCheckStatus();
						return;
					}

					// NIF-720: JWT caduca con el proceso → 1002/1003. Si hay redirect_cancelled, redirigir
					// (NIF-697) en lugar del tile «Proceso caducado» de token.
					if (this.tryRedirectOnSessionTokenExpired(error)) {
						this.unSubscribeCheckStatus();
						return;
					}

					// Detener el streaming si el dispositivo no está en INPROGRESS
					if (this.isStreamingActive) {
						this.streamingManagementService.stopStreaming();
						this.isStreamingActive = false;
					}
					// Ante error al consultar estado, detener capturas periódicas por seguridad.
					this.screenShotService.stopSaveScreenshot();

					if (this.errorService.hasGeneralError(error.error)) {
						this.unSubscribeCheckStatus();
					}

					this.cloudLogsService.sendCloudLogs(
						{ getProcessStatus: `Error after 3 retries ${error?.error}` },
						ProcessLog.IN_PROCESS,
						LogType.Error,
					);
				},
			});
	}

	/**
	 * Sincroniza la captura periódica de screenshots con el estado real del dispositivo.
	 * Solo debe estar activa cuando el backend marca el dispositivo como `IN_PROGRESS`.
	 */
	private syncScreenshotCaptureForDeviceStatus(status: PROCESS_STATUS): void {
		if (status !== PROCESS_STATUS.INPROGRESS) this.screenShotService.stopSaveScreenshot();
	}

	/**
	 * Inicia un intervalo para verificar si el audio sigue sonando
	 * Cada 500ms revisa si isPlaying es false, y cuando lo es, setea el estado
	 */
	private startAudioCheckInterval(newState: PROCESS_STATUS): void {
		// Limpiar intervalo anterior si existe
		if (this.audioCheckInterval) {
			clearInterval(this.audioCheckInterval);
		}

		this.audioCheckInterval = setInterval(() => {
			// Si el audio dejó de sonar, setear el estado y limpiar
			if (!this.audioService.isPlaying) {
				console.error('Audio is not playing. New State:', newState);
				this.process.status_process_device = newState;
				this.stopAudioCheckInterval();
				if (newState === PROCESS_STATUS.CANCELLED) {
					this.maybeRedirectOnExpired();
				}
			}
		}, 500); // Cada 500ms
	}

	/**
	 * Detiene el intervalo de verificación de audio
	 */
	private stopAudioCheckInterval(): void {
		if (this.audioCheckInterval) {
			clearInterval(this.audioCheckInterval);
			this.audioCheckInterval = null;
		}
	}

	private getConfig(): void {
		void this.loadProcessConfig();
	}

	private fetchProcessConfig(): void {
		this.processService.getConfig().subscribe({
			next: async (processConfig: IProcessConfigResponse) => {
				this.pendingTwoFactorChannel = null;
				// this.updateConfigConnections(processConfig);
				this.errorService.clearErrorUnlessDevToolsBlocked();
				// Actualizar el token si viene en la respuesta
				if (processConfig.jwt) this.auth.setAccessToken(processConfig.jwt);
				this.process = processConfig;
				this.resumedFromConfigLoad = this.isResumeContext(processConfig);
				if (processConfig.status_process_device === PROCESS_STATUS.INPROGRESS) {
					this.isDeviceOnStandby = false;
				}
				this.languageService.setCurrentProcessId(this.process.process);

				// CRITICAL: Update baseStreamId in streaming service
				// This is called once at app startup and will be used for all streamId generation
				this.streamingManagementService.setBaseStreamId(processConfig?.process);

				// Config Logs
				this.cloudLogsService.setProcessUUID(this.process.process);
				this.cloudLogsService.setDeviceId(this.auth.getDeviceId());
				this.cloudLogsService.setDeviceType(this.process.primary_device ? 'primary' : 'secondary');
				if (this.blockIfProcessAlreadyActiveInAnotherTab()) {
					this.operations = [];
					this.operationService.setOperations([]);
					this.loadingService.hide();
					return;
				}

				this.auth.setQRUrl(processConfig.pwcs_url);
				if (this.process.design) {
					this.applyProcessDesign(this.process.design, 'fallback');
				}
				this.configService.setConfig(this.process.config);

				// Configurar con el config del proceso para el manejo de constraints y audio
				this.cameraService.updateRecordingConfig(this.process.config.required_recording);

				// Configurar el SDK según el flag del proceso
				this.identitySDKService.setEnabled(!!this.process.config?.sdk);

				this.operations = this.operationService.convertProcessBackToFront(this.process);
				this.operationService.setOperations(this.operations);
				this.process.status_process_device = resolveDeviceStatusForExpiry(
					this.process.status_process_device,
					processConfig.process_status_code,
				);
				if (this.process.status_process_device === PROCESS_STATUS.CANCELLED && this.maybeRedirectOnExpired()) {
					this.loadingService.hide();
					return;
				}
				this.loadTaskVision();
				this.loadingInProgress(processConfig.status_process);

				if (processConfig.device_registered !== false && !this.deviceRegisterCompleted) {
					this.deviceRegisterCompleted = true;
					this.processService.markDeviceRegistered();
				}

				this.checkCameraPermissions();
				// Precargar audios
				await this.preloadAudios();
				this.evaluateTwoFactorModal();
				// Pausa durante TFA; al salir (verificado o sin 2fa) arranca master + location + status polling.
				if (!this.isTwoFactorGateActive()) {
					this.startMasterLocationAndStatusPolling();
				}
			},
			error: (error: HttpErrorResponse) => {
				const errorCode = error?.error?.error?.error_code;
				if (errorCode === 1107) {
					this.deviceRegisterCompleted = false;
					this.processService.resetDeviceRegistrationGate();
					void this.loadProcessConfig();
					return;
				}
				if (this.handleTwoFactorPendingConfigError(error)) {
					return;
				}
				this.errorService.hasGeneralError({
					...error?.error,
					action: () => {
						void this.getInitialData();
					},
				});
				this.cloudLogsService.sendCloudLogs(
					{ getInitialData: `Error: ${error?.error}` },
					ProcessLog.PRELOAD,
					LogType.Error,
				);
				this.loadingService.hide();
			},
		});
	}

	private updateConfig(): void {
		this.processService
			.getConfigDesign()
			.pipe(catchError(() => of(null)))
			.subscribe({
				next: (design) => {
					if (design) {
						this.applyProcessDesign(design);
					}
					this.loadingService.show(this.trans.instant('GENERAL.LOADING.CONFIG'));
					this.processService.getConfig().subscribe({
						next: async (processConfig: IProcessConfigResponse) => {
							// this.updateConfigConnections(processConfig);
							if (processConfig.jwt && !processConfig.primary_device) {
								this.auth.setAccessToken(processConfig.jwt);
							}
							this.process = processConfig;
							this.resumedFromConfigLoad = this.isResumeContext(processConfig);
							this.languageService.setCurrentProcessId(this.process.process);

							// CRITICAL: Update baseStreamId in streaming service
							// This ensures baseStreamId is always current if config is updated
							this.streamingManagementService.setBaseStreamId(processConfig?.process);

							// Config Logs
							this.cloudLogsService.setProcessUUID(this.process.process);
							this.cloudLogsService.setDeviceId(this.auth.getDeviceId());
							this.cloudLogsService.setDeviceType(this.process.primary_device ? 'primary' : 'secondary');
							if (this.blockIfProcessAlreadyActiveInAnotherTab()) {
								this.operations = [];
								this.operationService.setOperations([]);
								this.loadingService.hide();
								return;
							}

							this.auth.setQRUrl(processConfig.pwcs_url);
							if (this.process.design) {
								this.applyProcessDesign(this.process.design, 'fallback');
							}
							this.configService.setConfig(this.process.config);

							// Configurar con el config del proceso para el manejo de constraints y audio
							this.cameraService.updateRecordingConfig(this.process.config.required_recording);

							this.operations = this.operationService.convertProcessBackToFront(this.process);
							this.operationService.setOperations(this.operations);
							this.loadingInProgress(processConfig.status_process);
							if (this.isDeviceOnStandby) this.isDeviceOnStandby = false;
							if (processConfig.device_registered !== false) {
								this.deviceRegisterCompleted = true;
								this.processService.markDeviceRegistered();
							}
							// Reanudar streaming si es requerido
							if (this.process.config.required_recording) this.resumeStreaming();
							// Precargar audios
							await this.preloadAudios();
							this.languageService.applyLanguageForCurrentProcess();
							this.evaluateTwoFactorModal();
							if (!this.isTwoFactorGateActive()) {
								this.startMasterLocationAndStatusPolling();
							}
						},
						error: (error: HttpErrorResponse) => {
							if (this.handleTwoFactorPendingConfigError(error)) {
								return;
							}
							this.errorService.hasGeneralError({
								...error?.error,
								action: () => {
									this.getConfig();
								},
							});
							this.cloudLogsService.sendCloudLogs(
								{ getInitialData: `Error: ${error?.error}` },
								ProcessLog.PRELOAD,
								LogType.Error,
							);
							this.loadingService.hide();
						},
					});
				},
			});
	}

	private async checkProcessStatus(): Promise<void> {
		return new Promise((resolve, reject) => {
			this.processService.checkProcessStatus().subscribe({
				next: (response: IProcessCheckStatusResponse) => {
					// Si viene token en la respuesta, actualizarlo para las siguientes llamadas
					if (response?.token) {
						const tkn = response.token.replace('Bearer ', '');
						// Token de acceso para el resto de llamadas
						this.auth.setAccessToken(tkn);
						// Token para el qr de cambiar de dispositivo
						this.auth.setAuthToken(tkn);
					}
					resolve();
				},
				error: () => {
					// Si hay error, que continúe el flujo normal de la aplicación,
					// 	pero se rechaza la promesa para que no espere más
					resolve();
				},
			});
		});
	}

	private cancelDevices(): void {
		this.loadingService.show(this.trans.instant('GENERAL.LOADING.CONFIG'));
		this.processService.cancelDevices().subscribe({
			next: () => {
				this.loadingService.hide();
				this.updateConfig();
			},
			error: (error: HttpErrorResponse) => {
				this.errorService.hasGeneralError({
					...error?.error,
					action: () => {
						this.cancelDevices();
					},
				});
				this.cloudLogsService.sendCloudLogs(
					{ cancelDevices: `Error: ${error?.error}` },
					ProcessLog.PRELOAD,
					LogType.Error,
				);
				this.loadingService.hide();
			},
		});
	}

	private getMaster(): void {
		this.masterService.getMasterFromService().subscribe({
			next: (masterData: IMaster) => {
				this.masterService.setMaster(masterData);
				this.languageService.setAllowedLanguages(masterData.languages.map((lang) => lang.name));
				this.languageService.applyLanguageForCurrentProcess();
			},
			error: (error: HttpErrorResponse) => {
				this.errorService.hasGeneralError({
					...error?.error,
					action: () => {
						this.getMaster();
					},
				});
				this.cloudLogsService.sendCloudLogs(
					{ getMaster: `Error: ${error?.error}` },
					ProcessLog.PRELOAD,
					LogType.Error,
				);
				this.loadingService.hide();
			},
		});
	}

	/**
	 * Precarga los audios necesarios basándose en las operaciones disponibles.
	 * Siempre precarga FINISH (pantalla final); además OCR y/o POL si están en el proceso.
	 *
	 * @returns Una promesa que se resuelve cuando todas las precargas de audio han finalizado.
	 */
	private preloadAudios(): Promise<void> {
		const operations = this.process.operations;
		const hasOcrOperations = operations.find((op) => op.type === OPERATION_TYPE.OCR);
		const hasPolOperations = operations.find((op) => op.type === OPERATION_TYPE.POL);

		const preloadPromises: (() => Promise<void>)[] = [async () => await this.audioService.preloadFinishAudios()];
		if (hasOcrOperations) {
			preloadPromises.push(async () => await this.audioService.preloadOcrMrzAudios());
		}
		if (hasPolOperations) {
			preloadPromises.push(async () => await this.audioService.preloadLifeCheckAudios());
		}

		return Promise.all(preloadPromises.map((fn) => fn())).then(() => {});
	}

	/**
	 * Gets the constraints for a specific operation type
	 * @param operationType The type of operation (OCR, POL, etc.)
	 * @returns MediaStreamConstraints for the operation
	 */
	private getConstraintsForOperation(operationType: OPERATION_TYPE): MediaStreamConstraints {
		switch (operationType) {
			case OPERATION_TYPE.OCR:
				return this.ocrService.constraints;
			case OPERATION_TYPE.POL:
				return this.lifeCheckService.constraints;
			default:
				// Default to OCR constraints for other operations
				return this.ocrService.constraints;
		}
	}

	/**
	 * Determina si un tipo de operación requiere streaming
	 * Solo OCR y POL necesitan streaming activo
	 */
	private operationRequiresStreaming(operationType: OPERATION_TYPE | null | undefined): boolean {
		if (!operationType) return false;
		return operationType === OPERATION_TYPE.OCR || operationType === OPERATION_TYPE.POL;
	}

	private resumeStreaming(): void {
		const currentOperation = this.operations.find((op: IOperation) => !op.completed);
		if (!currentOperation || !this.operationRequiresStreaming(currentOperation.type)) return;
		this.handleStreamingForOperation(undefined, currentOperation);
	}

	private async loadDocumentModel(): Promise<void> {
		if (!this.tasksVision) {
			await this.loadTaskVision();
		}
		if (!this.tasksVision) {
			return;
		}

		try {
			// Misma heurística Adreno que Face Landmarker (POL): GPU defectuosa → CPU (NIF-827).
			const delegateResolution = resolveMediapipeDelegate();
			if (delegateResolution.delegate === 'CPU') {
				this.cloudLogsService.sendCloudLogs(
					{
						loadDocumentModel: {
							delegate: 'CPU',
							reason: delegateResolution.reason,
							webGlRenderer: delegateResolution.webGlRenderer,
							adrenoModel: delegateResolution.adrenoModel,
						},
					},
					ProcessLog.PRELOAD,
					LogType.Info,
				);
			}

			const documentModel: ObjectDetector = await ObjectDetector.createFromOptions(this.tasksVision, {
				baseOptions: {
					modelAssetPath: `./assets/models/document.tflite`,
					delegate: delegateResolution.delegate,
				},
				maxResults: 2,
				scoreThreshold: 0.6,
				runningMode: this.runningMode,
			});
			this.processHelperService.setDocumentModel(documentModel);
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ loadDocumentModel: `Error: ${error?.toString()}` },
				ProcessLog.PRELOAD,
				LogType.Error,
			);
		}
	}

	private loadOpenCV(): void {
		if (!this.tasksVision) {
			this.loadTaskVision();
		}
		cv.then((openCv: any) => {
			this.processHelperService.setOpenCV(openCv);
		});
	}

	private async loadFaceModel(): Promise<void> {
		if (!this.tasksVision) {
			await this.loadTaskVision();
		}
		if (!this.tasksVision) {
			return;
		}

		try {
			const delegateResolution = resolveMediapipeDelegate();
			// NIF-1091: log siempre (CPU y GPU) para correlacionar stalls en reintentos PoL.
			this.cloudLogsService.sendCloudLogs(
				{
					loadFaceModel: {
						delegate: delegateResolution.delegate,
						reason: delegateResolution.reason,
						webGlRenderer: delegateResolution.webGlRenderer,
						adrenoModel: delegateResolution.adrenoModel,
						maliModel: delegateResolution.maliModel,
						runningMode: this.runningMode,
					},
				},
				ProcessLog.PRELOAD,
				LogType.Info,
			);

			const faceLandmarker = await FaceLandmarker.createFromOptions(this.tasksVision, {
				baseOptions: {
					modelAssetPath: `./assets/models/face_landmarker.task`,
					delegate: delegateResolution.delegate,
				},
				outputFaceBlendshapes: true,
				runningMode: this.runningMode,
				minFaceDetectionConfidence: 0.7,
				minFacePresenceConfidence: 0.7,
				numFaces: 2,
			});
			this.processHelperService.setFaceModel(faceLandmarker);
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ loadFaceModel: `Error: ${error?.toString()}` },
				ProcessLog.PRELOAD,
				LogType.Error,
			);
		}
	}

	private async loadImageEmbedderModel(): Promise<void> {
		if (!this.tasksVision) {
			await this.loadTaskVision();
		}
		if (!this.tasksVision) {
			return;
		}
		try {
			const imageEmbedder: ImageEmbedder = await ImageEmbedder.createFromOptions(this.tasksVision, {
				baseOptions: {
					modelAssetPath: `./assets/models/mobilenet_v3_small.tflite`,
				},
				runningMode: this.runningMode,
				// quantize: true
			});
			this.processHelperService.setImageEmbedderModel(imageEmbedder);
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ loadImageEmbedderModel: `Error: ${error?.toString()}` },
				ProcessLog.PRELOAD,
				LogType.Error,
			);
		}
	}

	private async loadTaskVision(): Promise<void> {
		try {
			this.tasksVision = await FilesetResolver.forVisionTasks(MEDIAPIPE_TASKS_VISION_WASM_CDN);
			if (this.operations?.length) {
				if (this.operations.find((operation) => operation.type === OPERATION_TYPE.OCR)) {
					await this.loadDocumentModel();
				}
				if (this.operations.find((operation) => operation.type === OPERATION_TYPE.OCR_CANNY)) {
					// this.loadOpenCV();
				}
				if (this.operations.find((operation) => operation.type === OPERATION_TYPE.POL)) {
					await Promise.all([this.loadFaceModel(), this.loadImageEmbedderModel()]);
				}
			}
		} catch (error: unknown) {
			this.cloudLogsService.sendCloudLogs(
				{ loadTaskVision: `Error: ${error instanceof Error ? error.message : String(error)}` },
				ProcessLog.PRELOAD,
				LogType.Error,
			);
		}
	}

	get operation(): any {
		if (!this.operations.length || this.isDeviceOnStandby) return null;
		// NIF-969: preferir cualquier paso incompleto que no sea FINISH (orden/flags incorrectos).
		const incomplete = this.operations.filter((operation: IOperation) => !operation.completed);
		const currentOperation: IOperation | undefined =
			incomplete.find((operation: IOperation) => operation.type !== OPERATION_TYPE.FINISH) ?? incomplete[0];

		const result =
			this.process?.status_process_device !== PROCESS_STATUS.STANDBY &&
			this.process?.status_process_device !== PROCESS_STATUS.OK &&
			this.process?.status_process_device !== PROCESS_STATUS.KO &&
			this.process?.status_process_device !== PROCESS_STATUS.CANCELLED &&
			currentOperation
				? currentOperation
				: null;

		// Detectar cambio de operación y disparar el manejo de streaming.
		// El componente HOME completa la operación mutando directamente operation.completed,
		// por lo que la única forma de detectar la transición es desde este getter
		// (Angular lo llama en cada ciclo de change detection).
		if (result?.type !== this.lastOperationType) {
			const previousType = this.lastOperationType;
			this.lastOperationType = result?.type ?? null;
			if (result) {
				const previousOperation = this.operations.find((op: IOperation) => op.type === previousType);
				this.handleStreamingForOperation(previousOperation, result);
			}
		}

		return result;
	}

	/**
	 * Visibilidad del warning diferido:
	 * - `start_and_resume` (o vacío legacy): se muestra siempre al iniciar/cargar el proceso.
	 * - `resume_only`: se muestra solo cuando la config llega con operaciones ya completadas (resume real)
	 *   y únicamente la primera vez por proceso en la sesión actual.
	 */
	public get showDelayedProcessWarning(): boolean {
		if (!this.process?.config?.delayed_process_warning) return false;

		const visibility = this.getDelayedWarningVisibility();
		if (visibility !== 'resume_only') return true;

		if (!this.resumedFromConfigLoad) {
			return false;
		}
		return !this.isResumeOnlyWarningSeen();
	}

	/** Lee visibilidad desde metadata (nuevo) y mantiene fallback legacy en config. */
	private getDelayedWarningVisibility(): 'start_and_resume' | 'resume_only' {
		const metadata = this.process?.config?.metadata;
		if (
			metadata &&
			typeof metadata === 'object' &&
			(metadata as Record<string, unknown>)['delayed_process_warning_visibility'] === 'resume_only'
		) {
			return 'resume_only';
		}
		return this.process?.config?.delayed_process_warning_visibility === 'resume_only'
			? 'resume_only'
			: 'start_and_resume';
	}

	private getResumeOnlyWarningSeenStorageKey(): string | null {
		const processId = this.process?.process;
		return processId ? `pwcs.resume_only_warning_seen.${processId}` : null;
	}

	/**
	 * Reanudación = `status_process === IN_PROGRESS` (mismo criterio que antes de NIF-650).
	 * La 1.ª visita recibe INIT en el primer getConfig (claim a IN_PROGRESS al final en back).
	 */
	private isResumeContext(processConfig: Pick<IProcessConfigResponse, 'status_process'>): boolean {
		return processConfig?.status_process === PROCESS_STATUS.INPROGRESS;
	}

	private isResumeOnlyWarningSeen(): boolean {
		const key = this.getResumeOnlyWarningSeenStorageKey();
		if (!key) return false;
		return sessionStorage.getItem(key) === '1';
	}

	private markResumeOnlyWarningSeen(): void {
		const key = this.getResumeOnlyWarningSeenStorageKey();
		if (!key) return;
		sessionStorage.setItem(key, '1');
	}

	public onDelayedProcessWarningClosed(): void {
		if (this.getDelayedWarningVisibility() === 'resume_only') {
			this.markResumeOnlyWarningSeen();
		}
	}

	private getFinishOperation(): IFinishOperation | undefined {
		const ops = this.operations?.length ? this.operations : this.process?.operations;
		return pickFinishOperation(ops);
	}

	/**
	 * Caducado / enlace expirado (NIF-762): copy custom de FINISH `cancelled_*` o i18n legacy de proceso.
	 */
	public cancelledProcessTitle(): string {
		return resolveFinishResultMessage(
			(v, opts) => this.languageService.resolveLocalizedText(v, opts),
			(key) => this.trans.instant(key),
			this.getFinishOperation()?.config?.cancelled_title,
			'GENERAL.PROCESS.LINK_EXPIRED_TITLE',
		);
	}

	public cancelledProcessDescription(): string {
		return resolveFinishResultMessage(
			(v, opts) => this.languageService.resolveLocalizedText(v, opts),
			(key) => this.trans.instant(key),
			this.getFinishOperation()?.config?.cancelled_description,
			'GENERAL.PROCESS.LINK_EXPIRED_DESCRIPTION',
		);
	}

	/** Fallback genérico de sesión caducada (@default del switch sin operación). */
	public expiredProcessTitle(): string {
		return resolveFinishResultMessage(
			(v, opts) => this.languageService.resolveLocalizedText(v, opts),
			(key) => this.trans.instant(key),
			this.getFinishOperation()?.config?.cancelled_title,
			'GENERAL.PROCESS.EXPIRED_TITLE',
		);
	}

	public expiredProcessDescription(): string {
		return resolveFinishResultMessage(
			(v, opts) => this.languageService.resolveLocalizedText(v, opts),
			(key) => this.trans.instant(key),
			this.getFinishOperation()?.config?.cancelled_description,
			'GENERAL.PROCESS.EXPIRED_DESCRIPTION',
		);
	}

	/** Misma semántica que `FinishComponent.shouldCheckDevice`: hay URL de salida configurada. */
	private hasFinishRedirects(): boolean {
		return finishHasRedirectUrls(this.getFinishOperation());
	}

	/**
	 * Redirect caducado (misma UX que FINISH OK/KO: tile 3s y luego `window.location`).
	 * Reglas de elegibilidad: `shouldScheduleExpiredRedirect`.
	 */
	private maybeRedirectOnExpired(): boolean {
		const input = {
			primaryDevice: this.process?.primary_device,
			statusProcessDevice: this.process?.status_process_device,
			processStatusCode: this.process?.process_status_code,
			redirectCancelled: this.getFinishOperation()?.config?.redirect_cancelled,
		};
		if (!shouldScheduleExpiredRedirect(input)) {
			return false;
		}
		if (this.expiredRedirectScheduled) {
			return true;
		}
		this.expiredRedirectScheduled = true;
		timer(FINISH_REDIRECT_DELAY_MS)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.goToExternalUrl(input.redirectCancelled);
			});
		return true;
	}

	/**
	 * JWT expirado (1002/1003) con URL de caducado ya cargada en FINISH: misma UX que cron 1098.
	 * Evita el tile GENERAL.ERROR.*_TOKEN («Proceso caducado») cuando NIF-720 alinea JWT y proceso.
	 */
	private tryRedirectOnSessionTokenExpired(error: HttpErrorResponse): boolean {
		const code = Number(error?.error?.error?.error_code);
		if (code !== 1002 && code !== 1003) {
			return false;
		}
		if (!this.process?.primary_device) {
			return false;
		}
		if (!this.getFinishOperation()?.config?.redirect_cancelled) {
			return false;
		}
		this.process.status_process_device = PROCESS_STATUS.CANCELLED;
		this.process.process_status_code = PROCESS_EXPIRED_DETAILS_CODE;
		return this.maybeRedirectOnExpired();
	}

	/** Salida del journey (FINISH OK/KO y caducado). Extraído para tests. */
	private goToExternalUrl(url: string): void {
		window.location.href = url;
	}

	private syncProcessStatusCode(code: number | null | undefined): void {
		if (this.process && code !== undefined) {
			this.process.process_status_code = code;
		}
	}

	public openModalConfirm(): void {
		const dialogMessage = {
			title: this.trans.instant('GENERAL.PROCESS.CANCELLED_MODAL_TITLE'),
			description: this.trans.instant('GENERAL.PROCESS.CANCELLED_MODAL_DESCRIPTION'),
			buttonPrimaryLabel: this.trans.instant('GENERAL.BUTTON.CONTINUE'),
			buttonSecondaryLabel: this.trans.instant('GENERAL.BUTTON.CANCEL'),
		};
		this.dialogConfirmationService.show(dialogMessage).subscribe((event: IModalClose) => {
			if (event === 'buttonPrimary') {
				if (this.hasFinishRedirects()) {
					this.cancelDevices();
				} else {
					this.updateConfig();
				}
			}
			this.dialogConfirmationService.hide();
		});
	}

	/**
	 * Canal 2FA según `process['2fa']` en la raíz (`false` | `sms` | `whatsapp`).
	 */
	public get twoFactorModalTfaType(): 'sms' | 'whatsapp' | null {
		const c = this.getBackend2faChannel();
		if (c === 'sms' || c === 'whatsapp') {
			return c;
		}
		return this.pendingTwoFactorChannel;
	}

	/** TFA pendiente: no lanzar master, location ni polling de status hasta verificar. */
	private isTwoFactorGateActive(): boolean {
		if (this.twoFactorVerified) {
			return false;
		}
		if (this.isTwoFactorModalVisible || this.pendingTwoFactorChannel) {
			return true;
		}
		const ch = this.getBackend2faChannel();
		return ch === 'sms' || ch === 'whatsapp';
	}

	/** Arranca master, geolocalización y polling de `/process/status` (omitido mientras TFA pendiente). */
	private startMasterLocationAndStatusPolling(): void {
		this.getMaster();
		this.getLocation();
		if (this.checkStatusPollingStarted) return;
		this.checkStatusPollingStarted = true;
		this.initCheckStatus();
	}

	/**
	 * Lee el flag 2FA en la raíz de la respuesta (`IProcessConfigResponse['2fa']`).
	 * Normaliza strings para tolerar mayúsculas u otros formatos.
	 */
	private getBackend2faChannel(): 'sms' | 'whatsapp' | false | undefined {
		if (!this.process) {
			return undefined;
		}
		const raw = this.process['2fa'];
		if (raw === false) {
			return false;
		}
		if (raw === undefined || raw === null) {
			return undefined;
		}
		if (typeof raw === 'string') {
			const n = raw.toLowerCase().trim();
			if (n === 'sms' || n === 'whatsapp') {
				return n;
			}
		}
		return undefined;
	}

	/**
	 * Abre el modal 2FA cuando el backend envía `2fa: sms|whatsapp` en la raíz (p. ej. tras `getConfig`).
	 * NIF-892: no abrir (ni disparar bootstrapSend/OTP) si el proceso está caducado o el device CANCELLED.
	 */
	private evaluateTwoFactorModal(): void {
		if (isProcessExpiredCode(this.process?.process_status_code)) {
			return;
		}
		if (this.process?.status_process_device === PROCESS_STATUS.CANCELLED) {
			return;
		}
		const channel = this.getBackend2faChannel();
		if (channel !== 'sms' && channel !== 'whatsapp') {
			return;
		}
		if (this.twoFactorVerified || this.isTwoFactorModalVisible) {
			return;
		}
		this.openTwoFactorModal();
	}

	private openTwoFactorModal(): void {
		if (this.twoFactorVerified || this.isTwoFactorModalVisible) return;
		this.isTwoFactorModalVisible = true;
	}

	/**
	 * NIF-969: tras 2FA recargar config para refrescar `operations[].completed`
	 * (NIF-398 no recargaba; el snapshot previo podía dejar FINISH como único incompleto).
	 * El polling master/location/status lo arranca `updateConfig` cuando el gate TFA ya no aplica.
	 */
	public onTwoFactorVerified(): void {
		this.twoFactorVerified = true;
		this.isTwoFactorModalVisible = false;
		this.pendingTwoFactorChannel = null;
		if (this.process) this.process['2fa'] = false;
		this.updateConfig();
	}

	/** NIF-969: Finish montado con pasos pendientes → recargar config y volver al paso real. */
	public onFinishIncompleteOperations(): void {
		this.updateConfig();
	}

	/**
	 * NIF-892: `tfa/send` o `sms-code` devolvieron 1005 (proceso caducado/terminado).
	 * Cierra el modal y muestra tile caducado / redirect_cancelled.
	 */
	public onTwoFactorProcessFinished(): void {
		this.isTwoFactorModalVisible = false;
		this.pendingTwoFactorChannel = null;
		if (this.process) {
			this.process['2fa'] = false;
			this.process.status_process_device = PROCESS_STATUS.CANCELLED;
			this.process.process_status_code = PROCESS_EXPIRED_DETAILS_CODE;
		}
		this.maybeRedirectOnExpired();
	}

	/**
	 * Si el backend bloquea rutas protegidas con 2102, abre el modal 2FA en lugar de dejar la pantalla en blanco.
	 * NIF-892: no abrir si el proceso ya está caducado/CANCELLED.
	 */
	private handleTwoFactorPendingConfigError(error: HttpErrorResponse): boolean {
		const errorCode = Number(error?.error?.error?.error_code);
		if (errorCode !== TWO_FACTOR_PENDING_ERROR_CODE) {
			return false;
		}
		if (
			isProcessExpiredCode(this.process?.process_status_code) ||
			this.process?.status_process_device === PROCESS_STATUS.CANCELLED
		) {
			return false;
		}
		const tfaType = error?.error?.error?.tfa_type;
		if (tfaType !== 'sms' && tfaType !== 'whatsapp') {
			return false;
		}
		this.pendingTwoFactorChannel = tfaType;
		this.process = { '2fa': tfaType } as IProcessConfigResponse;
		this.loadingService.hide();
		this.openTwoFactorModal();
		return true;
	}

	private updateProcessStatus(status: PROCESS_STATUS): void {
		this.processService.updateProcessStatus(status).subscribe({
			next: () => {
				this.process.status_process = status;
			},
			error: (error: HttpErrorResponse) => {
				this.errorService.hasGeneralError({
					...error?.error,
					action: () => {
						this.updateProcessStatus(status);
					},
				});
				this.cloudLogsService.sendCloudLogs(
					{ updateProcessStatus: 'Error' },
					ProcessLog.PRELOAD,
					LogType.Error,
				);
				this.loadingService.hide();
			},
		});
	}

	/**
	 * Preload: solo consulta Permissions API si existe (sin getUserMedia).
	 * El stream real se abre en VideoComponent de cada operación para evitar doble prompt.
	 */
	private checkCameraPermissions(): void {
		if (
			!this.operations?.length ||
			(!this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.OCR) &&
				!this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.POL))
		) {
			return;
		}
		const permissions = navigator.permissions;
		if (!permissions?.query) {
			return;
		}
		permissions
			.query({ name: 'camera' as PermissionName })
			.then(() => undefined)
			.catch(() => undefined);
	}

	public unSubscribeCheckStatus(): void {
		this.checkStatus$.next();
		this.checkStatus$.complete();
		this.checkStatus$ = new Subject<void>();
		this.checkStatusPollingStarted = false;
	}

	/**
	 * Pide la ubicación al navegador al cargar el proceso y la persiste en back.
	 * HOME solo espera el resultado cuando `location` es `mandatory`.
	 */
	private getLocation(): void {
		if (!this.isLocationFeatureActive()) return;
		if ('geolocation' in navigator) {
			// El servicio reintenta si el usuario acepta el permiso tras TIMEOUT/denegación inicial.
			this.geolocationPermissionService.requestPosition(
				({ latitude, longitude }) => {
					this.sendLocation(latitude, longitude);
				},
				(error: GeolocationPositionError) => {
					// Solo errores definitivos (p. ej. posición no disponible); TIMEOUT no llega aquí.
					console.error('Error obteniendo la ubicación:', error);
					this.cloudLogsService.sendCloudLogs(
						{ location: `Error obteniendo la ubicación: ${error.message}` },
						ProcessLog.PRELOAD,
						LogType.Error,
					);
				},
			);
		} else {
			this.cloudLogsService.sendCloudLogs(
				{ location: 'geolocalization is not available' },
				ProcessLog.PRELOAD,
				LogType.Error,
			);
		}
	}

	/** `enabled` y `mandatory` activan la geolocalización; `disabled` la omite. */
	private isLocationFeatureActive(): boolean {
		const mode = this.process.config?.location;
		return mode === 'enabled' || mode === 'mandatory';
	}

	private sendLocation(lat: number, lon: number): void {
		// POST /location; si es mandatory, HOME espera isLocationSaved para continuar.
		this.processService.sendLocation({ lat: lat?.toString(), lon: lon?.toString() }).subscribe({
			next: () => {
				const foundIndex: number = this.process?.operations?.findIndex(
					(op: IOperation) => op.type === OPERATION_TYPE.HOME,
				);
				if (foundIndex > -1) {
					(this.process.operations[foundIndex] as IHomeOperation).config.isLocationSaved = true;
				}
			},
			error: (error: HttpErrorResponse) => {
				if (this.process.config.location === 'mandatory') {
					this.errorService.hasGeneralError({
						...error?.error,
						action: () => {
							this.sendLocation(lat, lon);
						},
					});
				}
				this.cloudLogsService.sendCloudLogs({ sendLocation: 'Error' }, ProcessLog.PRELOAD, LogType.Error);
			},
		});
	}

	/**
	 * Spinner «Retomando proceso...» cuando el config llega ya en IN_PROGRESS (reapertura).
	 * 1.ª visita: back devuelve INIT en el payload y luego hace el claim → no se muestra.
	 */
	private loadingInProgress(status: PROCESS_STATUS): void {
		if (status === PROCESS_STATUS.INPROGRESS) {
			const loadingMessage = this.trans.instant('GENERAL.LOADING.PROCESS_IN_PROGRESS');
			this.loadingService.show(loadingMessage);
			/*
				Resvisamos si el mensaje que se muestra es el mismo que el de mensaje en proceso en curso.
				Si es así, esperamos 2 segundos y ocultamos el loading.
				Si no es así, no eliminamos el loading ya que se estará gestionando por otro proceso.
			*/
			setTimeout(() => {
				this.loadingService
					.loading()
					.pipe(take(1))
					.subscribe((loading) => {
						if (loading.text === loadingMessage) {
							this.loadingService.hide();
						}
					});
			}, 2000);
		} else {
			this.loadingService.hide();
		}
	}

	private blockIfProcessAlreadyActiveInAnotherTab(): boolean {
		const processUuid = this.process?.process?.trim();
		const deviceId = this.auth.getDeviceId()?.trim();
		if (!processUuid || !deviceId) {
			return false;
		}

		const result = this.processTabGuard.tryActivateProcess(processUuid, deviceId);
		if (result.blocked) {
			this.loadingService.hide();
			if (this.process?.config) {
				this.process.config.delayed_process_warning = false;
			}
			this.errorService.setErrorGeneral({
				code: ERROR_STATUS.ACCESS,
				error: {
					error_code: ERROR_CODE.PROCESS_ACTIVE_IN_OTHER_TAB,
					msg: '',
				},
				action: () => this.continueProcessInThisTabAfterTakeover(),
			});
			this.cloudLogsService.sendCloudLogs(
				{
					processTabGuard: {
						blocked: true,
						processUuid,
						deviceId,
						ownerTabId: result.record?.tabId,
					},
				},
				ProcessLog.PRELOAD,
				LogType.Warning,
			);
			return true;
		}

		return false;
	}

	private continueProcessInThisTabAfterTakeover(): void {
		const processUuid = this.process?.process?.trim();
		const deviceId = this.auth.getDeviceId()?.trim();
		if (!processUuid || !deviceId) {
			return;
		}

		this.processTabGuard.takeoverProcess(processUuid, deviceId);
		this.errorService.setErrorGeneral(null);
		this.getConfig();
	}

	private releaseProcessTabOwnership(): void {
		const processUuid = this.process?.process?.trim();
		if (!processUuid) return;
		this.processTabGuard.releaseProcess(processUuid);
	}

	/**
	 * Streaming con `required_recording`: OCR y POL comparten el mismo `streamId` (véase `generateStreamId()` → `baseStreamId`),
	 * por lo que al pasar de OCR a POL con streaming ya activo **no** se llama a `changeOperationStreamId` (evita reabrir WebSocket innecesariamente).
	 * La transición solo ejecuta `handleCameraChangeIfNeeded` si cambia `facingMode` (p. ej. documento → selfi); en ese caso se hace
	 * `updateStream` en el servicio. `changeOperationStreamId` queda disponible si en el futuro se usan streamIds distintos por operación.
	 */
	private async handleStreamingForOperation(
		previousOperation?: IOperation,
		currentOperation?: IOperation,
	): Promise<void> {
		// Guard: Prevenir ejecuciones concurrentes
		if (this.isHandlingStreaming) return;

		this.isHandlingStreaming = true;
		const startTime = Date.now();

		// Safety timeout: liberar el guard después de 20 segundos para evitar deadlocks
		const guardTimeout = setTimeout(() => {
			if (this.isHandlingStreaming) this.isHandlingStreaming = false;
		}, 20000);

		try {
			// Verificar que this.process esté cargado antes de continuar
			if (!this.process) return;

			// Verificar si se debe manejar streaming
			const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();

			// Si no se requiere grabación, detener streaming si está activo
			if (!isRecordingRequired) {
				if (this.isStreamingActive) await this.stopStreaming();
				return;
			}

			// Si se requiere grabación, verificar la operación actual
			if (isRecordingRequired) {
				const isOcrOrPol =
					currentOperation?.type === OPERATION_TYPE.OCR || currentOperation?.type === OPERATION_TYPE.POL;
				const isFinish = currentOperation?.type === OPERATION_TYPE.FINISH;

				// Si la operación no es ni OCR, ni POL, ni FINISH (ej: HOME, CONNECTIONS)
				// Y hay streaming activo → detenerlo (OCR/POL NO consecutivos)
				if (!isOcrOrPol && !isFinish) {
					if (this.isStreamingActive) await this.stopStreaming();
					return;
				}

				if (isFinish) {
					// Detener streaming si es FINISH
					if (this.isStreamingActive) await this.stopStreaming();
					return;
				}

				// Manejar inicio/cambio de streaming para OCR o POL
				if (isOcrOrPol) {
					const newStreamId = this.generateStreamId();

					// Sincronizar estado con el servicio
					this.isStreamingActive = this.streamingManagementService.isStreamingActive();

					// Si no hay streaming activo, iniciarlo
					if (!this.isStreamingActive) {
						await this.startStreaming(newStreamId);
						return;
					}

					// Si el streaming está activo, verificar si necesitamos cambiar la cámara
					await this.handleCameraChangeIfNeeded(currentOperation);
				}
			}
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{
					streaming: `handleStreamingForOperation: ERROR - ${error?.toString()}`,
					errorStack: error?.stack,
					currentOp: currentOperation?.type,
					duration: Date.now() - startTime,
				},
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
		} finally {
			clearTimeout(guardTimeout);
			this.isHandlingStreaming = false;
		}
	}

	/**
	 * Genera un streamId único para operaciones de streaming
	 * OCR y POL comparten el MISMO streamId para evitar reiniciar la conexión WebRTC
	 * cuando se transiciona entre estas operaciones consecutivas
	 * Incluye el device_id para evitar solapamientos entre dispositivos
	 */
	/**
	 * Operaciones que montan `app-video` y pueden heredar stream del servicio.
	 */
	private operationUsesCamera(operationType: OPERATION_TYPE | undefined): boolean {
		if (!operationType) return false;
		return (
			operationType === OPERATION_TYPE.OCR ||
			operationType === OPERATION_TYPE.OCR_CANNY ||
			operationType === OPERATION_TYPE.POL ||
			operationType === OPERATION_TYPE.DOCUMENTS
		);
	}

	/**
	 * Al cambiar de operación con cámara (OCR↔POL, solo POL, POL→OCR, etc.):
	 * si el stream heredado está muerto, liberarlo sin `changeCamera` para que el nuevo
	 * `VideoComponent` haga un único `getUserMedia`. Si sigue vivo, `handleCameraChangeIfNeeded`
	 * ajusta `facingMode` cuando aplique.
	 */
	private prepareCameraHandoffForOperationTransition(
		previousOperation: IOperation | undefined,
		currentOperation: IOperation,
	): void {
		if (!this.operationUsesCamera(currentOperation.type)) return;
		if (previousOperation?.type === currentOperation.type) return;
		if (!this.cameraService.isCameraStreamUnusable()) return;
		this.cameraService.releaseStreamForOperationHandoff();
	}

	/**
	 * Verifica y maneja el cambio de cámara si las constraints lo requieren
	 */
	private async handleCameraChangeIfNeeded(currentOperation: IOperation): Promise<void> {
		const newConstraints = this.getConstraintsForOperation(currentOperation.type);
		const currentStream = this.cameraService.stream;

		if (!currentStream || this.cameraService.isCameraStreamUnusable(currentStream)) {
			return;
		}

		const videoTrack = currentStream.getVideoTracks()[0];
		if (!videoTrack) return;

		const currentSettings = videoTrack.getSettings();
		const newVideoConstraints = newConstraints.video as MediaTrackConstraints;

		// Extraer facingMode de las nuevas constraints
		const newFacingMode = this.extractFacingMode(newVideoConstraints?.facingMode);

		// Si el facingMode no cambia, no hacer nada
		if (!newFacingMode || currentSettings?.facingMode === newFacingMode) return;

		// Cambiar la cámara
		try {
			// 1. Detener el stream actual para liberar la cámara
			currentStream.getTracks().forEach((track) => track.stop());

			// 2. Anular el stream: el app-video del componente hijo llamará getUserMedia.
			this.cameraService.stream = null;

			// 3. Esperar reactivamente a que el hijo emita el nuevo stream (máx 15s).
			const newStream = await new Promise<MediaStream | null>((resolve) => {
				const timer = setTimeout(() => {
					sub.unsubscribe();
					resolve(null);
				}, 15_000);
				const sub = this.cameraService.stream$.subscribe((s) => {
					if (s && s.active) {
						clearTimeout(timer);
						sub.unsubscribe();
						resolve(s);
					}
				});
			});

			if (!newStream || !newStream.active) {
				this.cloudLogsService.sendCloudLogs(
					{ streaming: 'Camera change: new stream not available after 15 seconds' },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
				return;
			}

			// 4. Reemplazar el track en WebRTC sin cortar la conexión de streaming.
			await this.streamingManagementService.updateStream(newStream);

			// 5. Reiniciar MediaRecorder sobre el nuevo stream (los tracks anteriores se pararon; el recorder seguiría roto si no).
			await this.streamingManagementService.restartRecordingOnStream(newStream);
		} catch (error) {
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error changing camera: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
		}
	}

	/**
	 * Extrae el facingMode de las constraints
	 */
	private extractFacingMode(constraint: any): string | undefined {
		if (typeof constraint === 'string') return constraint;
		if (constraint && typeof constraint === 'object' && 'ideal' in constraint) {
			return constraint.ideal;
		}
		return undefined;
	}

	private generateStreamId(): string {
		const baseId = this.streamingManagementService.getBaseStreamId();
		if (!baseId) {
			throw new Error('Cannot generate streamId: baseStreamId not available');
		}
		return baseId;
	}

	private async startStreaming(streamId: string): Promise<void> {
		try {
			// Esperar reactivamente a que el stream de cámara esté disponible Y activo (máximo 15s).
			// NO marcamos isStreamingActive=true todavía: si lo hacemos antes de tener el stream,
			// el app-video component omite getUserMedia y el stream nunca llega => deadlock.
			let stream = this.cameraService.stream;

			if (!stream || !stream.active) {
				stream = await new Promise<MediaStream | null>((resolve) => {
					const timer = setTimeout(() => {
						sub.unsubscribe();
						resolve(null);
					}, 15_000);
					const sub = this.cameraService.stream$.subscribe((s) => {
						if (s && s.active) {
							clearTimeout(timer);
							sub.unsubscribe();
							resolve(s);
						}
					});
				});
			}

			if (!stream || !stream.active) {
				this.cloudLogsService.sendCloudLogs(
					{ streaming: 'Camera stream not available or not active after 15 seconds' },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
				return;
			}

			// Solo marcamos activo una vez que tenemos el stream real
			this.isStreamingActive = true;

			const success = await this.streamingManagementService.startStreaming(streamId, stream);

			if (success) {
				this.currentStreamId = streamId; // Guardar el streamId actual
				this.notifyStreamingStartedOnce();
			} else {
				// Marcar como NO activo si falla
				this.isStreamingActive = false;
				this.cloudLogsService.sendCloudLogs(
					{ streaming: 'Failed to start WebRTC streaming - startStreaming returned false' },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
				this.errorService.setStreamingError(async () => {
					await this.handleStreamStart(streamId);
				});
			}
		} catch (error: any) {
			this.isStreamingActive = false;
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error starting streaming: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			this.errorService.setStreamingError(async () => {
				await this.handleStreamStart(streamId);
			});
		}
	}

	private async handleStreamStart(streamId: string): Promise<void> {
		// Mismo path que OCR/POL: republicar WHIP + esperar outbound (no solo startStreaming).
		await this.streamingManagementService.retryStreaming(() => {
			this.errorService.clearError();
		});
		this.isStreamingActive = true;
		this.currentStreamId = this.streamingManagementService.getCurrentStreamId() ?? streamId;
		this.notifyStreamingStartedOnce();
	}

	/** NIF-1064: POST /process/streaming/started una vez por proceso (fail-soft). */
	private notifyStreamingStartedOnce(): void {
		if (this.streamingStartedNotified) {
			return;
		}
		this.streamingStartedNotified = true;
		this.processService
			.markStreamingStarted()
			.pipe(
				catchError((err: unknown) => {
					this.cloudLogsService.sendCloudLogs(
						{ streaming: `markStreamingStarted failed: ${String(err)}` },
						ProcessLog.IN_PROCESS,
						LogType.Error,
					);
					return of(null);
				}),
			)
			.subscribe();
	}

	private async stopStreaming(): Promise<void> {
		try {
			await this.streamingManagementService.stopStreaming();
			this.isStreamingActive = false;
			this.currentStreamId = null; // Limpiar el streamId
		} catch (error: any) {
			// Error deteniendo streaming
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error stopping streaming: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
		}
	}

	ngOnDestroy(): void {
		if (this.designAssetRefreshTimer !== null) {
			clearTimeout(this.designAssetRefreshTimer);
		}
		// Cancela watchers de geolocalización si el usuario sale.
		this.geolocationPermissionService.stop();
		this.releaseProcessTabOwnership();
		this.destroy$.next();
		this.destroy$.complete();
		this.unSubscribeCheckStatus();
		void this.streamingManagementService.destroy();
	}
}
